import { zodResolver } from "@hookform/resolvers/zod";
import {
  KikMarketingCloudDoubleOptInProcedures,
  kikPasswordResetSendEmailVerificationFieldsSchema,
  KikPasswordResetWithActionCodeFields,
  kikPasswordResetWithActionCodeFieldsSchema,
  KikUserZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import Button from "~/components/common/button";
import richTextStyle from "~/components/common/rich-text.module.scss";
import Checkbox, { CheckboxVariants } from "~/components/form/checkbox";
import Errors from "~/components/form/errors";
import InputField from "~/components/form/input-field";
import inputFieldStyles from "~/components/form/input-field.module.scss";
import { useActionCodeContext } from "~/contexts/action-code";
import { useAuthContext } from "~/contexts/auth";
import { useFormTranslations } from "~/hooks/use-form-translations";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import Logger from "~/services/logger/logger";
import styles from "~/styles/pages/password-reset-request.module.scss";
import appRoutes from "~/utils/app-routes";
import crossCountryUtils from "~/utils/cross-country-utils";

const formSchema = kikPasswordResetSendEmailVerificationFieldsSchema.pick({
  EmailAddress: !0,
});

type FormData = z.infer<typeof formSchema>;

export default function PasswordResetStep2({ onPrev, email }: { onPrev: () => void; email: string }) {
  const t = useTranslations();
  const { timeLeft, resetTimer } = useActionCodeContext();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<KikPasswordResetWithActionCodeFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    /**
     * In this scenario I decided to add EmailAddress to the schema, but the schema is refined,
     * so I cannot omit the field and need to use a hidden field
     */
    resolver: zodResolver(kikPasswordResetWithActionCodeFieldsSchema),
    defaultValues: { EmailAddress: email },
  });
  const { authError, isLoading, passwordResetWithActionCode, sendEmailVerification } = useAuthContext();

  const currentCountry = crossCountryUtils.getCurrentCountryCode(router);
  const currentLanguage = crossCountryUtils.getCurrentLanguageCode(router);
  const doSendEmailVerification = (data: FormData = getValues()) => {
    sendEmailVerification({
      ...data,
      CountryID: currentCountry,
      LanguageID: currentLanguage,
      procedure: KikMarketingCloudDoubleOptInProcedures.PASSWORD_RESET,
    }).then(() => {
      resetTimer();
      Logger.instance.info("PasswordResetStep1: sent email");
    });
  };
  const { getFieldError } = useFormTranslations();
  const { newSuccessGlobalNotification } = useGlobalNotification();

  const onSubmit: SubmitHandler<KikPasswordResetWithActionCodeFields> = (
    data: KikPasswordResetWithActionCodeFields
  ) => {
    Logger.instance.info("PasswordResetStep2 submit", data);
    passwordResetWithActionCode(data).then(() => {
      newSuccessGlobalNotification(
        {
          slug: "passwordResetConfirmation",
          closeOnClickCta: true,
          ctaCaption: "generic.close",
        },
        {
          timeout: 10000,
        }
      );
      reset();
      router.push(appRoutes.LOGIN);
    });
  };

  const [showPassword, setShowPassword] = useState<boolean>(!1);
  return (
    <>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit, (data) => Logger.instance.warn("PasswordResetStep2 invalid data", data))}
        className={styles.formContainer}
      >
        <p className={richTextStyle.paragraph}>
          {t.rich("generic.signUpDoubleOptInDesc", {
            br: () => <br />,
          })}
        </p>
        <InputField
          {...register("EmailAddress")}
          type="email"
          readOnly
          label={t("generic.form.EmailAddress")}
          className={styles.input}
        />
        <InputField
          {...register("actionCode")}
          type="text"
          label={t("generic.form.action_code")}
          error={getFieldError(errors.actionCode?.message, {
            fieldName: t("generic.form.action_code"),
            minLength: KikUserZodSchemas.actionCodeLength,
          })}
          isSubmitting={isSubmitting}
          maxLength={KikUserZodSchemas.actionCodeLength}
          onClearErrors={() => clearErrors("actionCode")}
          className={styles.input}
        />
        <InputField
          {...register("password")}
          type={showPassword ? "text" : "password"}
          label={t("generic.form.password_label")}
          error={getFieldError(errors.password?.message, {
            fieldName: t("generic.form.password_label"),
            minLength: KikUserZodSchemas.passwordMinlength,
          })}
          buttonName={
            <Checkbox
              label={t("generic.form.show_hide_password")}
              variant={CheckboxVariants.visibility}
              width={16}
              height={16}
              customStyles={{
                icon: inputFieldStyles.iconvisibility,
                container: inputFieldStyles.containerShowHidePassowrdCheckbox,
                labelCheckbox: inputFieldStyles.labelShowHidePassowrdCheckbox,
              }}
              checked={showPassword}
              onChange={() => setShowPassword(!showPassword)}
            />
          }
          onClearErrors={() => clearErrors("password")}
        />
        <InputField
          {...register("confirmPassword")}
          type={showPassword ? "text" : "password"}
          label={t("generic.form.confirm_password_label")}
          error={getFieldError(errors.confirmPassword?.message, {
            fieldName: t("generic.form.confirm_password_label"),
            minLength: KikUserZodSchemas.passwordMinlength,
          })}
          buttonName={
            <Checkbox
              label={t("generic.form.show_hide_password")}
              variant={CheckboxVariants.visibility}
              width={16}
              height={16}
              customStyles={{
                icon: inputFieldStyles.iconvisibility,
                container: inputFieldStyles.containerShowHidePassowrdCheckbox,
                labelCheckbox: inputFieldStyles.labelShowHidePassowrdCheckbox,
              }}
              checked={showPassword}
              onChange={() => setShowPassword(!showPassword)}
            />
          }
          onClearErrors={() => clearErrors("confirmPassword")}
        />
        <span className={styles.mandatory}>{t("generic.form.mandatory_long")}</span>
        {timeLeft ? (
          <p className={richTextStyle.paragraph}>
            {t.rich("generic.signUpDoubleOptInSendAnotherIn", {
              seconds: timeLeft,
            })}
          </p>
        ) : (
          <p className={richTextStyle.paragraph}>
            {t.rich("generic.signUpDoubleOptInActionCodeNotReceived", {
              sendAnother: (chunks) => (
                <Button
                  className={richTextStyle.link}
                  lookAsLink
                  disabled={isLoading}
                  onClick={() => doSendEmailVerification()}
                >
                  {chunks}
                </Button>
              ),
            })}
          </p>
        )}
        <div className={styles.buttonsWrapper}>
          <Button type="button" variant="secondary" className={styles.button} onClick={() => onPrev()}>
            {t("generic.back")}
          </Button>
          <Button type="submit" variant="primary" className={styles.button} loading={isLoading} disabled={isSubmitting}>
            {t("generic.submit")}
          </Button>
        </div>
      </form>
      <Errors error={authError}></Errors>
    </>
  );
}
